/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, string, object, func } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import {
  CANCEL,
  SEND_OFFER,
  ACCEPT_OFFER,
  UPDATE_OFFER,
  cancelDiscussion as cancelDiscussionAction,
  acceptOffer as acceptOfferAction,
} from 'actions/inbox';

import {
  REQUEST_TALENT,
  UNSAVE_TALENT,
  REQUEST_TALENT_SUCCESS,
  REQUEST_TALENT_FAILURE,
  requestTalent as requestTalentAction,
  unsaveTalent as unsaveTalentAction,
} from 'actions/job';

import Button from 'components/Button/Button';

import styles from './message-actions.scss';

/**
 * This is the MessageActions component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class MessageActions extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    user: object.isRequired,
    history: object.isRequired,
    discussionId: string.isRequired,
    jobId: string.isRequired,
    talentId: string.isRequired,
    firstButtonType: string.isRequired,
    secondButtonType: string.isRequired,
    cancelDiscussion: func.isRequired,
    acceptOffer: func.isRequired,
    requestTalent: func.isRequired,
    unsaveTalent: func.isRequired,
    callbackPath: string,
  };

  static defaultProps = {
    callbackPath: null,
  };

  onCancel = () => {
    const {
      auth: { token },
      discussionId,
      cancelDiscussion,
    } = this.props;

    if (window.confirm(p.t('general.confirm_cancel'))) {
      cancelDiscussion(token, discussionId);
    }
  };

  onAcceptOffer = () => {
    const {
      auth: { token },
      discussionId,
      acceptOffer,
    } = this.props;

    acceptOffer(token, discussionId);
  };

  onSendOffer = () => {
    const { history, discussionId, callbackPath } = this.props;

    history.push(createUrl('updateOffer', discussionId), {
      callbackPath,
    });
  };

  onUpdateOffer = () => {
    const { history, discussionId, callbackPath } = this.props;

    history.push(createUrl('updateOffer', discussionId), {
      callbackPath,
    });
  };

  onRequestTalent = () => {
    const {
      history,
      auth: { token },
      jobId,
      talentId,
      requestTalent,
    } = this.props;

    requestTalent(token, jobId, talentId).then(({ type, payload }) => {
      if (type === REQUEST_TALENT_SUCCESS) {
        // Go to disussion page.
        history.push(createUrl('message', payload.discussion_id));
      }

      if (type === REQUEST_TALENT_FAILURE) {
        console.log('ERROR', payload); // @TODO: Handle error.
      }
    });
  };

  onUnsaveTalent = () => {
    const {
      auth: { token },
      jobId,
      talentId,
      unsaveTalent,
    } = this.props;

    unsaveTalent(token, jobId, talentId);
  };

  renderButton(type, dark = false, ghost = false) {
    let isDarkButton = dark;
    let isGhostButton = ghost;
    let isWarning = false;
    let onClick = null;
    const { user } = this.props;

    switch (type) {
      case CANCEL:
        onClick = this.onCancel;
        break;
      case SEND_OFFER:
        onClick = this.onSendOffer;
        break;
      case ACCEPT_OFFER:
        onClick = this.onAcceptOffer;
        break;
      case UPDATE_OFFER:
        if (user?.is_talent) return;
        onClick = this.onUpdateOffer;
        break;
      case REQUEST_TALENT:
        onClick = this.onRequestTalent;
        break;
      case UNSAVE_TALENT:
        isWarning = true;
        onClick = this.onUnsaveTalent;
        break;
      default:
        return null;
    }

    return (
      <div className={styles.button}>
        <Button
          dark={isDarkButton}
          onClick={onClick}
          small
          wide
          ghost={isGhostButton}
          warning={isWarning}
          className={styles.buttonOveride}
        >
          {p.t(`inbox.button_types.${type.toLowerCase()}`)}
        </Button>
      </div>
    );
  }

  /**
   * Render MessageActions.
   *
   * @return {Object}
   */
  render() {
    const { firstButtonType, secondButtonType } = this.props;

    return (
      <div className={styles.wrapper}>
        {this.renderButton(firstButtonType, true, true)}
        {this.renderButton(secondButtonType, true, false)}
      </div>
    );
  }
}

export default withRouter(
  connect(({ auth, user }) => ({ auth, user }), {
    cancelDiscussion: cancelDiscussionAction,
    acceptOffer: acceptOfferAction,
    requestTalent: requestTalentAction,
    unsaveTalent: unsaveTalentAction,
  })(MessageActions)
);
